// Every capture recorded against one operator-entered identifier, 20 at a time.
//
// One-shot reads, not a listener: this is a historical record, not a work
// queue, and a live subscription on a page you open to compare two photos
// costs reads for nothing.
//
// The identifier lives under two field names — mobile writes `actualTaskId`,
// the legacy migration writes `userVisibleId`, and a migrated workspace still
// using the app has both — so this runs two queries and interleaves them.
// Paging a merged result is not one cursor: each field's query carries its own
// `startAfter`, and a cursor only advances for the documents that page actually
// consumed. That is the k-way merge below.
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getFirestore, collection, query, where, orderBy, limit, startAfter,
  getDocs, getCountFromServer,
} from 'firebase/firestore';
import app from '../firebase';
import { toDate } from '../utils/taskCardModel';

/** Rows fetched per field, per page. Also the page size the caller sees. */
export const PAGE_SIZE = 20;

// secondaryTaskId: a Task ID field's answer on an asset template, shown under
// the asset's ID (utils/taskTitle.js): following it finds that work order too.
export const ID_FIELDS = ['actualTaskId', 'userVisibleId', 'secondaryTaskId'];

/**
 * The merge key, and deliberately NOT the util's `submittedAt || uploadedAt ||
 * createdAt` fallback: a k-way merge is only correct when it compares on the
 * same key the server sorted by. `orderBy('submittedAt')` also excludes any
 * document lacking the field, so every row that arrives here has one.
 */
function orderMs(task) {
  const d = toDate(task?.submittedAt);
  return d ? d.getTime() : Number.NEGATIVE_INFINITY;
}

export function baseConstraints(subscriberId, field, value) {
  return [
    where('subscriber_id', '==', subscriberId),
    where('deleted', '==', false),
    where(field, '==', value),
    orderBy('submittedAt', 'desc'),
  ];
}

export default function useTaskIdHistory({ subscriberId, value, pageSize = PAGE_SIZE }) {
  const [captures, setCaptures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  // Exact count of matching captures, or null when we never needed to ask or
  // cannot state one exactly (see fetchTotal).
  const [total, setTotal] = useState(null);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Per-field buffers and cursors. A ref, not state: the merge mutates these
  // many times inside one loadMore and no render should see the intermediates.
  const sourcesRef = useRef([]);
  const seenRef = useRef(new Set());
  const runRef = useRef(0);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);

  /** Fetch the next slice for one field. Mutates the source in place. */
  const refill = useCallback(async (db, source) => {
    if (source.exhausted) return;
    const constraints = baseConstraints(subscriberId, source.field, value);
    if (source.cursor) constraints.push(startAfter(source.cursor));
    const snap = await getDocs(query(
      collection(db, 'tasks'), ...constraints, limit(pageSize),
    ));
    const docs = snap.docs || [];
    source.buffer.push(...docs.map(d => ({ id: d.id, ...d.data() })));
    if (docs.length) source.cursor = docs[docs.length - 1];
    // A short read means the server has nothing left for this field.
    if (docs.length < pageSize) source.exhausted = true;
  }, [subscriberId, value, pageSize]);

  /**
   * Emit up to `need` rows in submittedAt order across both fields.
   *
   * A source whose buffer runs dry is refilled before the next comparison —
   * emitting from the other one first would be guessing that no unfetched
   * document is newer.
   */
  const drain = useCallback(async (db, need) => {
    const out = [];
    while (out.length < need) {
      for (const source of sourcesRef.current) {
        if (source.buffer.length === 0 && !source.exhausted) {
          // eslint-disable-next-line no-await-in-loop
          await refill(db, source);
        }
      }
      const ready = sourcesRef.current.filter(s => s.buffer.length > 0);
      if (ready.length === 0) break;

      const next = ready.reduce(
        (a, b) => (orderMs(b.buffer[0]) > orderMs(a.buffer[0]) ? b : a)
      );
      const task = next.buffer.shift();
      // A task carrying both field names matches both queries. It is one
      // capture and appears once.
      if (!seenRef.current.has(task.id)) {
        seenRef.current.add(task.id);
        out.push(task);
      }
    }
    return out;
  }, [refill]);

  const moreAvailable = () => sourcesRef.current.some(
    s => s.buffer.length > 0 || !s.exhausted
  );

  /**
   * The number of captures, asked for only when a page comes back full.
   *
   * A count aggregation is billed one document read per 1000 index entries it
   * matches, so this is 2 reads however long the history is — and an ID with
   * three captures never reaches this code, staying at three reads.
   *
   * Returns null when no exact figure is available: summing the two counts
   * would double-count a task carrying both field names. That is only possible
   * when both fields matched something, so a history sourced from one field
   * alone — the ordinary case — still gets an exact number.
   */
  const fetchTotal = useCallback(async (db) => {
    const counts = await Promise.all(sourcesRef.current.map(async (source) => {
      const snap = await getCountFromServer(query(
        collection(db, 'tasks'),
        ...baseConstraints(subscriberId, source.field, value),
      ));
      return snap.data().count;
    }));
    const nonEmpty = counts.filter(c => c > 0);
    return nonEmpty.length > 1 ? null : (nonEmpty[0] || 0);
  }, [subscriberId, value]);

  useEffect(() => {
    runRef.current += 1;
    const run = runRef.current;

    sourcesRef.current = ID_FIELDS.map(field => ({
      field, buffer: [], cursor: null, exhausted: false,
    }));
    seenRef.current = new Set();
    setCaptures([]);
    setTotal(null);
    setHasMore(false);

    if (!subscriberId || !value) {
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);

    (async () => {
      try {
        const db = getFirestore(app);
        const first = await drain(db, pageSize);
        if (runRef.current !== run) return;

        setCaptures(first);
        const more = moreAvailable();
        setHasMore(more);

        // Only pay for the count when the first page did not settle it.
        if (more) {
          const counted = await fetchTotal(db);
          if (runRef.current !== run) return;
          setTotal(counted);
        } else {
          setTotal(first.length);
        }
      } catch (e) {
        if (runRef.current !== run) return;
        console.error('Task ID history query failed:', e);
        setCaptures([]);
        setError(e.message || 'Could not load the capture history');
      } finally {
        if (runRef.current === run) setLoading(false);
      }
    })();

    return () => { runRef.current += 1; };
  }, [subscriberId, value, pageSize, reloadToken, drain, fetchTotal]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !moreAvailable()) return;
    const run = runRef.current;
    setLoadingMore(true);
    try {
      const db = getFirestore(app);
      const next = await drain(db, pageSize);
      if (runRef.current !== run) return;
      setCaptures(prev => [...prev, ...next]);
      setHasMore(moreAvailable());
    } catch (e) {
      if (runRef.current !== run) return;
      console.error('Task ID history page failed:', e);
      setError(e.message || 'Could not load more captures');
    } finally {
      if (runRef.current === run) setLoadingMore(false);
    }
  }, [drain, loading, loadingMore, pageSize]);

  // The page deletes through the same useTaskActions stack the list uses,
  // which removes the row optimistically. Without this the deleted capture
  // stays on screen until a reload.
  const removeCapture = useCallback((taskId) => {
    setCaptures(prev => prev.filter(t => t.id !== taskId));
    setTotal(t => (typeof t === 'number' ? Math.max(0, t - 1) : t));
  }, []);

  return {
    captures, loading, loadingMore, hasMore, total, error,
    loadMore, reload, removeCapture,
  };
}
